// Stops the app from reloading itself forever when the signed in account has no access
// to the tenant it is opening.
//
// The sugar Login view breaks out of the iframe with window.top.location pointing back at
// "/?tenant=<id>" whenever the request reaching index.php is not a logged in sugar user.
// For an SSO account that is not a member of that tenant the next load ends up on the same
// Login view again, so the whole app reloaded about once a second, each round firing the
// token-access calls that answer 401. Two guards break that:
//
// - every page load that arrives with "?tenant=" is counted per tenant in sessionStorage
//   (it survives the top level reload); a few of them in a short time without a single
//   sugar page loading in between is treated as "no access".
// - the token-access calls (JSONMenu, JSONMenu2, JSONAccessModuleList) retry a 401 / 403
//   at most twice with a growing delay and then report the tenant as denied.
//
// A denied tenant is not loaded into the iframe again during this page load, which is what
// ends the loop; TenantAccessDenied is shown instead.

const arrivalsKeyPrefix = 'tenantArrivals:';
const loopWindowMs = 20000;
const loopArrivalCount = 3;

export const accessRetryDelaysMs = [1500, 4000];

function readArrivals(tenantId) {
    try {
        const parsed = JSON.parse(sessionStorage.getItem(arrivalsKeyPrefix + tenantId) || '[]');
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
}

/**
 * Count this page load as an arrival on the tenant. Returns true when the tenant has been
 * arrived at so often in a short time that the app is caught in a reload loop.
 */
export function recordTenantArrival(tenantId, now = Date.now()) {
    if (!tenantId) {
        return false;
    }
    const arrivals = readArrivals(tenantId).filter(time => now - time < loopWindowMs);
    arrivals.push(now);
    try {
        sessionStorage.setItem(arrivalsKeyPrefix + tenantId, JSON.stringify(arrivals));
    } catch (e) {
        // storage full or blocked: the 401 retry limit still stops the loop
    }
    return arrivals.length >= loopArrivalCount;
}

/**
 * Forget the arrivals of one tenant (a sugar page loaded fine), or of all tenants
 * (a new login - the next account starts with a clean slate).
 */
export function clearTenantArrivals(tenantId) {
    try {
        if (tenantId) {
            sessionStorage.removeItem(arrivalsKeyPrefix + tenantId);
            return;
        }
        Object.keys(sessionStorage)
            .filter(key => key.indexOf(arrivalsKeyPrefix) === 0)
            .forEach(key => sessionStorage.removeItem(key));
    } catch (e) {
        // nothing stored
    }
}

// An account added to a tenant's group after its access token was issued does not see that
// tenant in /api/tenants (the list is filtered by the token's "groups" claim) until the token
// is renewed. Before a requested tenant is given up on, the token is renewed once; this
// limits that to once per tenant in a while, so a tenant that really is foreign cannot make
// the app renew over and over.
const renewalKeyPrefix = 'tenantAccessRenewal:';
export const accessRenewalIntervalMs = 5 * 60 * 1000;

/**
 * Returns true (and remembers it) when the access token may be renewed now for a tenant the
 * account does not seem to have; false when that was already tried a moment ago.
 */
export function claimAccessRenewal(tenantId, now = Date.now()) {
    if (!tenantId) {
        return false;
    }
    try {
        const last = Number(sessionStorage.getItem(renewalKeyPrefix + tenantId));
        if (!!last && now - last < accessRenewalIntervalMs) {
            return false;
        }
        sessionStorage.setItem(renewalKeyPrefix + tenantId, String(now));
        return true;
    } catch (e) {
        // without storage there is no way to tell a retry from the first try
        return false;
    }
}

/**
 * Drop "tenant=" from a query string when it names another tenant than the one opened.
 * The app falls back to the account's default tenant when the requested one is not
 * available; passing the requested tenant on to the default tenant's sugar made sugar
 * send the whole app back to "?tenant=<requested>" - a reload loop. Left as is while
 * the opened tenant is not known yet.
 */
export function withoutForeignTenant(search, tenantId) {
    if (!search || !tenantId) {
        return search || '';
    }
    const params = new URLSearchParams(search);
    const requested = params.get('tenant');
    if (!requested || requested === tenantId) {
        return search;
    }
    params.delete('tenant');
    const query = params.toString();
    return query ? '?' + query : '';
}

export function isAccessDeniedError(error) {
    const status = !!error && !!error.response ? error.response.status : null;
    return status === 401 || status === 403;
}

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Run request() and retry it on 401 / 403, at most accessRetryDelaysMs.length times with
 * those delays. Anything else is not retried. When the last attempt is still denied,
 * onDenied() is called and the error is passed on as usual.
 *
 * request is a factory so that every attempt builds its options again and picks up an
 * access token renewed in the meantime. shouldRetry() is asked before every retry, so a
 * tenant already known to be denied is not asked again.
 */
export function withAccessRetry(request, { onDenied, shouldRetry, delaysMs = accessRetryDelaysMs } = {}) {
    const attempt = (retryIndex) => request().catch(error => {
        if (!isAccessDeniedError(error)) {
            throw error;
        }
        const canRetry = retryIndex < delaysMs.length && (!shouldRetry || shouldRetry());
        if (canRetry) {
            return wait(delaysMs[retryIndex]).then(() => attempt(retryIndex + 1));
        }
        if (typeof onDenied === 'function') {
            onDenied(error);
        }
        throw error;
    });
    return attempt(0);
}
